import React, { useEffect, useState } from "react";

import { PanelWithHeader } from "../panels/PanelWithHeader";
import { TasksSummary } from "../todo/TasksSummary";
import { BodyWeightChart } from "../lifestyle/BodyWeightChart";
import { QuickLoansStatistics } from "../loans/QuickLoansStatistics";
import { QuickShoppingList } from "../shopping/QuickShoppingList";

import { PanelWithBreadCrumbAndSearchBox } from "../panels/PanelWithBreadCrumbAndSearchBox";
import {
  apiAddOrUpdateExpensesAndIncome,
  apiAddOrUpdateExpenseAndIncomeDefinition,
  apiBookExpenseAndIncomeDefinition,
  apiDeleteExpensesAndIncome,
  apiDeleteExpenseAndIncomeDefinition,
  apiGetExpenseAndIncomeDefinitions,
  apiGetExpensesAndIncomes,
} from "../../services/FinancialService";
import { DomikLoading } from "../common/DomikLoading";
import { FinanceAddOrUpdatePopup } from "../finance/FinanceAddOrUpdatePopup";
import "../finance/FinanceHomePage.css";

type FinanceHomePageProps = {
  value: string;
};

let config = require("../../configuration/config");

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultPeriod = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: toDateInputValue(firstDay),
    to: toDateInputValue(lastDay),
  };
};

const PAYDAY_DAY = 8;

// Pay period containing today: from the most recent payday to the next one
const getPaydayPeriod = () => {
  const now = new Date();
  let from: Date;
  let to: Date;
  if (now.getDate() >= PAYDAY_DAY) {
    from = new Date(now.getFullYear(), now.getMonth(), PAYDAY_DAY);
    to = new Date(now.getFullYear(), now.getMonth() + 1, PAYDAY_DAY);
  } else {
    from = new Date(now.getFullYear(), now.getMonth() - 1, PAYDAY_DAY);
    to = new Date(now.getFullYear(), now.getMonth(), PAYDAY_DAY);
  }
  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  };
};

export const FinanceHomePage = (props: FinanceHomePageProps) => {
  const [data, setData] = useState({ rows: [{}] });
  const [definitions, setDefinitions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [period, setPeriod] = useState(getDefaultPeriod());
  const [paydayMode, setPaydayMode] = useState(false);
  const [addOrUpdatePopupInformation, setAddOrUpdatePopupInformation] =
    useState<{ visible: boolean; item: any }>({ visible: false, item: undefined });

  console.log(config.serverUrl);

  useEffect(() => {
    ensureLatestData();
  }, []);

  let handlePopupClose = () => {
    setAddOrUpdatePopupInformation({ visible: false, item: undefined });
  };

  let handleAdd = () => {
    setAddOrUpdatePopupInformation({ visible: true, item: undefined });
  };

  let handleEdit = (item: any) => {
    setAddOrUpdatePopupInformation({ visible: true, item });
  };

  let handleDelete = (id: any) => {
    if (!window.confirm("Opravdu smazat tuto položku?")) {
      return;
    }
    apiDeleteExpensesAndIncome(id).then(() => {
      ensureLatestData();
    });
  };

  let handleDeleteDefinition = (id: any) => {
    if (!window.confirm("Opravdu smazat tuto opakující se platbu?")) {
      return;
    }
    apiDeleteExpenseAndIncomeDefinition(id).then(() => {
      ensureLatestData();
    });
  };

  let handleBook = (id: any) => {
    apiBookExpenseAndIncomeDefinition(id).then(() => {
      ensureLatestData();
    });
  };

  let handleSave = (formData: any) => {
    if (formData.isRepeatable) {
      const payload = {
        ...formData,
        created: formData.dueDate,
      };
      apiAddOrUpdateExpenseAndIncomeDefinition(payload).then(() => {
        handlePopupClose();
        ensureLatestData();
      });
    } else {
      const payload = {
        ...formData,
        created: formData.date,
      };
      apiAddOrUpdateExpensesAndIncome(payload).then(() => {
        handlePopupClose();
        ensureLatestData();
      });
    }
  };

  let ensureLatestData = () => {
    apiGetExpensesAndIncomes().then((response) => {
      setData({ rows: response.data });
      setLoading(false);
    });
    apiGetExpenseAndIncomeDefinitions().then((response) => {
      setDefinitions(response.data || []);
    });
  };

  let allCategories: string[] = [];

  if (data && data.rows && data.rows.length > 0) {
    data.rows.forEach((row: any) => {
      if (row.category) {
        if (allCategories.indexOf(row['category']) === -1) {
          allCategories.push(row.category);
        }
      }
    });
  }

  let periodFrom = period.from ? new Date(period.from + "T00:00:00") : null;
  let periodTo = period.to ? new Date(period.to + "T23:59:59") : null;

  let filteredRows = data.rows.filter((row: any) => {
    const matchesCategory =
      selectedCategory == "all" || row.category == selectedCategory;

    let matchesPeriod = true;
    if (row.created && (periodFrom || periodTo)) {
      const created = new Date(row.created);
      if (!isNaN(created.getTime())) {
        if (periodFrom && created < periodFrom) matchesPeriod = false;
        if (periodTo && created > periodTo) matchesPeriod = false;
      }
    }

    return matchesCategory && matchesPeriod;
  });

  let totalIncome = 0;
  let totalExpense = 0;

  filteredRows.forEach((row: any) => {
    if (row.isIncome) {
      totalIncome += row.amount || 0;
    } else {
      totalExpense += row.amount || 0;
    }
  });

  let balance = totalIncome - totalExpense;

  let formatAmount = (value: number) =>
    (value || 0).toLocaleString("cs-CZ") + " Kč";

  let formatDate = (value: any) => {
    if (!value) return "";
    const date = new Date(value);
    return isNaN(date.getTime())
      ? value
      : date.toLocaleDateString("cs-CZ");
  };

  // Group booked rows by day so the daily total is visible
  const dayKey = (value: any) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return "unknown";
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  };

  const groupsMap = new Map<string, any>();
  filteredRows.forEach((row: any) => {
    const key = row.created ? dayKey(row.created) : "unknown";
    if (!groupsMap.has(key)) {
      groupsMap.set(key, {
        key,
        date: row.created,
        rows: [],
        income: 0,
        expense: 0,
      });
    }
    const group = groupsMap.get(key);
    group.rows.push(row);
    if (row.isIncome) {
      group.income += row.amount || 0;
    } else {
      group.expense += row.amount || 0;
    }
  });

  const groupedRows = Array.from(groupsMap.values()).sort(
    (a: any, b: any) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  // Split recurring definitions into "due" (needs booking) and "upcoming"
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);

  let dueDefinitions = definitions
    .filter((def: any) => {
      const due = def.created ? new Date(def.created) : null;
      return due && !isNaN(due.getTime()) && due <= endOfToday;
    })
    .sort(
      (a: any, b: any) =>
        new Date(a.created).getTime() - new Date(b.created).getTime()
    );

  let upcomingDefinitions = definitions
    .filter((def: any) => {
      const due = def.created ? new Date(def.created) : null;
      return due && !isNaN(due.getTime()) && due > endOfToday;
    })
    .sort(
      (a: any, b: any) =>
        new Date(a.created).getTime() - new Date(b.created).getTime()
    );

  // Summary of upcoming regular (expense) payments within a time window
  const now = new Date();
  const endOfMonth = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );
  const paydayDate = period.to
    ? new Date(period.to + "T23:59:59")
    : endOfMonth;

  const countOccurrencesUntil = (def: any, until: Date) => {
    const due = def.created ? new Date(def.created) : null;
    if (!due || isNaN(due.getTime()) || due > until) return 0;
    if (!def.isRepeatable || !def.frequencyInDays || def.frequencyInDays <= 0) {
      return 1;
    }
    const days = Math.floor(
      (until.getTime() - due.getTime()) / (1000 * 60 * 60 * 24)
    );
    return Math.floor(days / def.frequencyInDays) + 1;
  };

  const sumRegularUntil = (until: Date) => {
    let sum = 0;
    definitions.forEach((def: any) => {
      if (def.isIncome) return; // count outgoing payments only
      sum += (def.amount || 0) * countOccurrencesUntil(def, until);
    });
    return sum;
  };

  const regularUntilMonthEnd = sumRegularUntil(endOfMonth);
  const regularUntilPayday = paydayMode ? sumRegularUntil(paydayDate) : 0;

  // Food & drink spending (main day-to-day expense) within the selected period
  const FOOD_CATEGORY = "Potraviny a pití";
  let foodExpense = 0;
  data.rows.forEach((row: any) => {
    if (row.isIncome || row.category !== FOOD_CATEGORY) return;
    if (row.created && (periodFrom || periodTo)) {
      const created = new Date(row.created);
      if (!isNaN(created.getTime())) {
        if (periodFrom && created < periodFrom) return;
        if (periodTo && created > periodTo) return;
      }
    }
    foodExpense += row.amount || 0;
  });

  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  let foodPerDay = 0;
  let foodForecast = foodExpense;
  if (periodFrom && periodTo) {
    const today = new Date();
    const elapsedEnd = today < periodTo ? today : periodTo;
    let daysElapsed =
      Math.floor((elapsedEnd.getTime() - periodFrom.getTime()) / MS_PER_DAY) + 1;
    if (daysElapsed < 1) daysElapsed = 1;
    let totalDays =
      Math.floor((periodTo.getTime() - periodFrom.getTime()) / MS_PER_DAY) + 1;
    if (totalDays < 1) totalDays = 1;
    foodPerDay = foodExpense / daysElapsed;
    foodForecast = foodPerDay * totalDays;
  }

  // Forecast: already booked expenses + upcoming regular payments
  // + predicted (not yet spent) food for the rest of the window
  const regularForecast = paydayMode ? regularUntilPayday : regularUntilMonthEnd;
  const foodRemainingForecast = Math.max(0, foodForecast - foodExpense);
  const forecastExpense =
    totalExpense + regularForecast + foodRemainingForecast;
  const forecastBalance = totalIncome - forecastExpense;
  const forecastLabel = paydayMode
    ? "Předpoklad do výplaty"
    : "Předpoklad do konce měsíce";

  return (
    <div className="container-fluid mt-3">
      {addOrUpdatePopupInformation.visible && (
        <FinanceAddOrUpdatePopup
          handleClose={handlePopupClose}
          handleConfirmation={handleSave}
          item={addOrUpdatePopupInformation.item}
        />
      )}
      <PanelWithBreadCrumbAndSearchBox currentPageName="Dashboard" />
      {loading && <DomikLoading />}
      {!loading && (
        <div className="row wow fadeIn">
          {/* Summary of booked payments for the selected period */}
          <div className="col-12 mb-4">
            <div className="financeSummaryBlock">
              <div className="financeSummaryBlock__header">
                <div className="financeSummaryBlock__title">
                  <i className="fas fa-receipt"></i> Sumář zaúčtovaných plateb
                </div>
                <div className="financeSummaryBlock__period">
                  <i className="far fa-calendar-alt"></i>
                  {period.from || period.to
                    ? `${formatDate(period.from)} – ${formatDate(period.to)}`
                    : "Celé období"}
                  <span className="financeSummaryBlock__count">
                    {filteredRows.length}{" "}
                    {filteredRows.length === 1 ? "platba" : "plateb"}
                  </span>
                </div>
              </div>
              <div className="financeSummary">
                <div className="financeSummary__card income">
                  <div className="financeSummary__icon">
                    <i className="fas fa-arrow-up"></i>
                  </div>
                  <div>
                    <div className="financeSummary__label">Příjmy</div>
                    <div className="financeSummary__value">
                      {formatAmount(totalIncome)}
                    </div>
                  </div>
                </div>
                <div className="financeSummary__card expense">
                  <div className="financeSummary__icon">
                    <i className="fas fa-arrow-down"></i>
                  </div>
                  <div>
                    <div className="financeSummary__label">Výdaje</div>
                    <div className="financeSummary__value">
                      {formatAmount(totalExpense)}
                    </div>
                    <div className="financeSummary__forecast">
                      <i className="fas fa-sync-alt"></i>
                      {forecastLabel}:{" "}
                      <span className="financeSummary__forecastValue expense">
                        {formatAmount(forecastExpense)}
                      </span>
                    </div>
                    <div className="financeSummary__forecast">
                      <i className="fas fa-utensils"></i>
                      Potraviny a pití:{" "}
                      <span className="financeSummary__forecastValue expense">
                        {formatAmount(foodExpense)}
                      </span>
                      <span className="financeSummary__forecastArrow">→</span>
                      předpoklad{" "}
                      <span className="financeSummary__forecastValue expense">
                        {formatAmount(Math.round(foodForecast))}
                      </span>
                      <span className="financeSummary__perDay">
                        ≈ {formatAmount(Math.round(foodPerDay))}/den
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  className={
                    "financeSummary__card balance" +
                    (balance < 0 ? " negative" : "")
                  }
                >
                  <div className="financeSummary__icon">
                    <i className="fas fa-wallet"></i>
                  </div>
                  <div>
                    <div className="financeSummary__label">Bilance</div>
                    <div className="financeSummary__value">
                      {formatAmount(balance)}
                    </div>
                    <div className="financeSummary__forecast">
                      <i className="fas fa-sync-alt"></i>
                      {forecastLabel}:{" "}
                      <span
                        className={
                          "financeSummary__forecastValue " +
                          (forecastBalance < 0 ? "expense" : "income")
                        }
                      >
                        {formatAmount(forecastBalance)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-12 mb-4">
            <PanelWithHeader
              header="Výdaje a příjmy"
              iconClassName="fas fa-chart-line"
              showAddIcon={true}
              handleAddAction={handleAdd}
            >
              {/* Period filter */}
              <div className="financePeriod">
                <div className="financePeriod__field">
                  <label htmlFor="periodFrom">Od</label>
                  <input
                    type="date"
                    id="periodFrom"
                    value={period.from}
                    onChange={(event) => {
                      setPaydayMode(false);
                      setPeriod({ ...period, from: event.target.value });
                    }}
                  />
                </div>
                <div className="financePeriod__field">
                  <label htmlFor="periodTo">Do</label>
                  <input
                    type="date"
                    id="periodTo"
                    value={period.to}
                    onChange={(event) => {
                      setPaydayMode(false);
                      setPeriod({ ...period, to: event.target.value });
                    }}
                  />
                </div>
                <button
                  type="button"
                  className={
                    "financePeriod__reset" + (!paydayMode ? " active" : "")
                  }
                  onClick={() => {
                    setPaydayMode(false);
                    setPeriod(getDefaultPeriod());
                  }}
                  title="Aktuální měsíc"
                >
                  <i className="fas fa-calendar-day"></i> Tento měsíc
                </button>
                <button
                  type="button"
                  className={
                    "financePeriod__reset" + (paydayMode ? " active" : "")
                  }
                  onClick={() => {
                    setPaydayMode(true);
                    setPeriod(getPaydayPeriod());
                  }}
                  title="Od výplaty do výplaty (10. den)"
                >
                  <i className="fas fa-money-check-alt"></i> Od výplaty
                </button>
              </div>

              {/* Category filter chips */}
              <div className="financeFilter">
                <button
                  className={
                    "financeChip" + (selectedCategory === "all" ? " active" : "")
                  }
                  onClick={() => setSelectedCategory("all")}
                >
                  Všechny
                </button>
                {allCategories.map((category) => (
                  <button
                    key={category}
                    className={
                      "financeChip" +
                      (selectedCategory === category ? " active" : "")
                    }
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>

              <div className="table-responsive">
                <table className="financeTable">
                  <thead>
                    <tr>
                      <th className="financeColSm">Datum</th>
                      <th className="financeColSm">Typ</th>
                      <th className="text-right">Částka</th>
                      <th>Popis</th>
                      <th>Kategorie</th>
                      <th>Místo</th>
                      <th className="text-right">Akce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 && dueDefinitions.length === 0 && (
                      <tr>
                        <td colSpan={7} className="financeTable__empty">
                          <i className="fas fa-inbox"></i>
                          <div>Žádné položky</div>
                        </td>
                      </tr>
                    )}
                    {dueDefinitions.map((def: any) => (
                      <tr
                        key={"def_" + def.expensesAndIncomesDefinitionId}
                        className="financeDueRow"
                      >
                        <td className="financeColSm financeDateCell">
                          {formatDate(def.created)}
                        </td>
                        <td className="financeColSm">
                          <span
                            className={
                              "financeBadge " +
                              (def.isIncome ? "income" : "expense")
                            }
                          >
                            <i
                              className={
                                "fas " +
                                (def.isIncome ? "fa-arrow-up" : "fa-arrow-down")
                              }
                            ></i>
                            {def.isIncome ? "Příjem" : "Výdej"}
                          </span>
                        </td>
                        <td
                          className={
                            "text-right financeAmountCell " +
                            (def.isIncome ? "income" : "expense")
                          }
                        >
                          {def.isIncome ? "+" : "-"}
                          {formatAmount(def.amount)}
                        </td>
                        <td className="financeDescCell">
                          <span className="financeDueBadge">
                            <i className="fas fa-clock"></i> Ke schválení
                          </span>{" "}
                          {def.description}
                        </td>
                        <td>
                          <span className="financeCategoryChip">
                            {def.category}
                          </span>
                        </td>
                        <td>{def.location}</td>
                        <td className="text-right">
                          <button
                            className="financeAction book"
                            title="Zaúčtovat"
                            onClick={() =>
                              handleBook(def.expensesAndIncomesDefinitionId)
                            }
                          >
                            <i className="fas fa-check"></i>
                          </button>
                          <button
                            className="financeAction edit"
                            title="Upravit"
                            onClick={() => handleEdit(def)}
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                          <button
                            className="financeAction delete"
                            title="Smazat"
                            onClick={() =>
                              handleDeleteDefinition(
                                def.expensesAndIncomesDefinitionId
                              )
                            }
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {groupedRows.map((group: any) => {
                      return (
                        <React.Fragment key={"grp_" + group.key}>
                          {group.rows.map((row: any, rowIndex: number) => (
                            <tr
                              key={row.expensesAndIncomesId}
                              className={
                                rowIndex === 0 ? "financeGroupStart" : ""
                              }
                            >
                              {rowIndex === 0 && (
                                <td
                                  className="financeColSm financeDateCell"
                                  rowSpan={group.rows.length}
                                >
                                  <div className="financeDateCell__inner">
                                    <i className="far fa-calendar"></i>
                                    <span>{formatDate(group.date)}</span>
                                    <span className="financeGroupCount">
                                      {group.rows.length} pol.
                                    </span>
                                  </div>
                                </td>
                              )}
                              <td className="financeColSm">
                                <span
                                  className={
                                    "financeBadge " +
                                    (row.isIncome ? "income" : "expense")
                                  }
                                >
                                  <i
                                    className={
                                      "fas " +
                                      (row.isIncome
                                        ? "fa-arrow-up"
                                        : "fa-arrow-down")
                                    }
                                  ></i>
                                  {row.isIncome ? "Příjem" : "Výdej"}
                                </span>
                              </td>
                              <td
                                className={
                                  "text-right financeAmountCell " +
                                  (row.isIncome ? "income" : "expense")
                                }
                              >
                                {row.isIncome ? "+" : "-"}
                                {formatAmount(row.amount)}
                              </td>
                              <td className="financeDescCell">
                                {row.description}
                              </td>
                              <td>
                                <span className="financeCategoryChip">
                                  {row.category}
                                </span>
                              </td>
                              <td>{row.location}</td>
                              <td className="text-right">
                                <button
                                  className="financeAction edit"
                                  title="Upravit"
                                  onClick={() => handleEdit(row)}
                                >
                                  <i className="fas fa-pen"></i>
                                </button>
                                <button
                                  className="financeAction delete"
                                  title="Smazat"
                                  onClick={() =>
                                    handleDelete(row.expensesAndIncomesId)
                                  }
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {filteredRows.length > 0 && (
                    <tfoot>
                      <tr className="financeTableFoot">
                        <td colSpan={2} className="financeTableFoot__label">
                          <i className="fas fa-calculator"></i> Celkem{" "}
                          {selectedCategory !== "all"
                            ? `(${selectedCategory})`
                            : `(${filteredRows.length} pol.)`}
                        </td>
                        <td className="text-right financeTableFoot__amount expense">
                          -{formatAmount(totalExpense)}
                        </td>
                        <td colSpan={4} className="financeTableFoot__extra">
                          <span className="income">
                            Příjmy: +{formatAmount(totalIncome)}
                          </span>
                          <span
                            className={
                              "bal" + (balance < 0 ? " negative" : "")
                            }
                          >
                            Bilance: {formatAmount(balance)}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {upcomingDefinitions.length > 0 && (
                <div className="financeUpcoming">
                  <div className="financeUpcoming__title">
                    <i className="fas fa-calendar-alt"></i> Nadcházející platby
                  </div>
                  <div className="financeUpcoming__list">
                    {upcomingDefinitions.map((def: any) => (
                      <div
                        key={"up_" + def.expensesAndIncomesDefinitionId}
                        className="financeUpcoming__item"
                      >
                        <div className="financeUpcoming__date">
                          <i className="far fa-clock"></i>
                          {formatDate(def.created)}
                        </div>
                        <div className="financeUpcoming__info">
                          <span className="financeUpcoming__desc">
                            {def.description || def.category}
                          </span>
                          {def.isRepeatable && def.frequencyInDays ? (
                            <span className="financeUpcoming__repeat">
                              <i className="fas fa-sync-alt"></i> každých{" "}
                              {def.frequencyInDays} dní
                            </span>
                          ) : null}
                        </div>
                        <div
                          className={
                            "financeUpcoming__amount " +
                            (def.isIncome ? "income" : "expense")
                          }
                        >
                          {def.isIncome ? "+" : "-"}
                          {formatAmount(def.amount)}
                        </div>
                        <div className="financeUpcoming__actions">
                          <button
                            className="financeAction book"
                            title="Zaúčtovat teď"
                            onClick={() =>
                              handleBook(def.expensesAndIncomesDefinitionId)
                            }
                          >
                            <i className="fas fa-check"></i>
                          </button>
                          <button
                            className="financeAction edit"
                            title="Upravit"
                            onClick={() => handleEdit(def)}
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                          <button
                            className="financeAction delete"
                            title="Smazat"
                            onClick={() =>
                              handleDeleteDefinition(
                                def.expensesAndIncomesDefinitionId
                              )
                            }
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Regular payments summary */}
              <div className="financeRegular">
                <div className="financeRegular__item">
                  <div className="financeRegular__label">
                    <i className="fas fa-sync-alt"></i> Pravidelné platby do
                    konce měsíce
                  </div>
                  <div className="financeRegular__value expense">
                    {formatAmount(regularUntilMonthEnd)}
                  </div>
                </div>
                {paydayMode && (
                  <div className="financeRegular__item highlight">
                    <div className="financeRegular__label">
                      <i className="fas fa-money-check-alt"></i> Ještě do výplaty
                      ({PAYDAY_DAY}.)
                    </div>
                    <div className="financeRegular__value expense">
                      {formatAmount(regularUntilPayday)}
                    </div>
                  </div>
                )}
              </div>
            </PanelWithHeader>
          </div>
        </div>
      )}
    </div>
  );
};
